import React, { useState, useEffect, useContext } from "react";
import PageTitle from "../../Components/PageTitle";
import axios from "axios";
import Swal from "sweetalert2";
import { ConfigContext } from "../../Context/ConfigContext";
import { ContentLoader, NoRecords } from "../../Components/Shimmer";
import { NavLink } from "react-router-dom";
import { DateFormaterMonth } from "../../Components/GlobalFunctions";
import { handleDownloadOptions } from "../../Components/ExportData";

const PendingProjects = () => {
    const { apiURL, apiHeaderJson, user_profile_id } = useContext(ConfigContext);

    const [projectData, setProjectData] = useState([]);
    const [loadingList, setLoadingList] = useState(false);

    // Get pending projects
    const getPendingProjects = async () => {
        setLoadingList(true);
        try {
            const response = await axios.get(
                `${apiURL}api/GetDashboardForConsultant`,
                {
                    params: { consultant_id: user_profile_id },
                    headers: apiHeaderJson
                }
            );

            const pendingProjects =
                response.data?.data?.tabs?.total_pending_projects || [];

            setProjectData(pendingProjects);
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Failed to load pending projects", "error");
        } finally {
            setLoadingList(false);
        }
    };

    // Download Handler
    const handleDownload = async (downloadType) => {
        const endpoint = `api/GetDashboardForConsultant`;
        const params = { consultant_id: user_profile_id };

        const columns = [
            { header: "Project Name", width: 30 },
            { header: "Location", width: 35 },
            { header: "Start Date", width: 23 },
            { header: "End Date", width: 23 },
            { header: "Status", width: 25 }
        ];

        const mapRow = (data) => ({
            "Project Name": data?.project_title ?? "-",
            "Location": data?.project_address ?? "-",
            "Start Date": DateFormaterMonth(data?.project_start_date) ?? "-",
            "End Date": DateFormaterMonth(data?.project_end_date) ?? "-",
            "Status": Labels(data?.project_status) ?? "-"
        });

        const responseHandler = (response) =>
            response.data?.data?.tabs?.total_pending_projects || [];

        try {
            await handleDownloadOptions(
                endpoint,
                params,
                columns,
                mapRow,
                downloadType,
                apiURL,
                apiHeaderJson,
                responseHandler
            );
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Failed to download report", "error");
        }
    };

    const Labels = (status) => {
        switch (status) {
            case 0:
                return "Active";
            case 1:
                return "Completed";
            case 2:
                return "Cancelled";
            default:
                return "-";
        }
    };

    const Classes = (status) => {
        switch (status) {
            case 0:
                return "badge bg-info-subtle text-info";
            case 1:
                return "badge bg-success-subtle text-success";
            case 2:
                return "badge bg-warning-subtle text-warning";
            default:
                return "badge bg-light";
        }
    };

    useEffect(() => {
        getPendingProjects();
    }, []);

    return (
        <div className="main-content">
            <div className="page-content">

                {loadingList ? (
                    <ContentLoader />
                ) : (
                    <div className="container-fluid">
                        <PageTitle title="Active Projects" primary="Home" />

                        <div className="col-md-12">
                            <div className="card">
                                <div
                                    className="card-header d-flex justify-content-between align-items-center"
                                    style={{ background: "#000000" }}
                                >
                                    <h4 className="card-title mb-0 text-white">
                                        All Active Projects List
                                    </h4>

                                    <div className="dropdown">
                                        <button
                                            type="button"
                                            className="btn btn-light btn-label waves-effect waves-light"
                                            id="dropdownMenuLink4"
                                            data-bs-toggle="dropdown"
                                        >
                                            <i className="ri-download-2-line label-icon align-middle fs-16 me-2" />
                                            Download Report
                                        </button>

                                        <ul
                                            className="dropdown-menu dropdown-menu-end"
                                            aria-labelledby="dropdownMenuLink4"
                                        >
                                            <li>
                                                <a
                                                    className="dropdown-item"
                                                    onClick={() => handleDownload("print")}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <i className="ri-printer-line me-2 text-muted" />
                                                    Print
                                                </a>
                                            </li>

                                            <li>
                                                <a
                                                    className="dropdown-item"
                                                    onClick={() => handleDownload("pdf")}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <i className="ri-file-pdf-line me-2 text-muted" />
                                                    PDF
                                                </a>
                                            </li>

                                            <li>
                                                <a
                                                    className="dropdown-item"
                                                    onClick={() => handleDownload("excel")}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <i className="ri-file-excel-2-line me-2 text-muted" />
                                                    Excel
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="card-body">
                                    <div className="table-responsive table-card">
                                        <table className="table table-bordered table-striped mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th className="text-center" width="35%">
                                                        Project Name
                                                    </th>
                                                    <th className="text-center" width="28%">
                                                        Location
                                                    </th>
                                                    <th className="text-center" width="18%">
                                                        Start Date
                                                    </th>
                                                    <th className="text-center" width="18%">
                                                        End Date
                                                    </th>
                                                    <th className="text-center" width="15%">
                                                        Status
                                                    </th>
                                                </tr>
                                            </thead>
                                        
                                            {projectData.length > 0 ? (
                                                <tbody>
                                                    {projectData.map((row) => (
                                                        <tr key={row.project_id}>
                                                            <td className="text-center">
                                                                <NavLink
                                                                    to={`/ProjectProfile/${row.project_id}`}
                                                                >
                                                                    {row?.project_title ?? "-"}
                                                                </NavLink>
                                                            </td>
                                        
                                                            <td className="text-center">
                                                                {row?.project_address ?? "-"}
                                                            </td>
                                        
                                                            <td className="text-center">
                                                                {DateFormaterMonth(
                                                                    row?.project_start_date
                                                                )}
                                                            </td>
                                        
                                                            <td className="text-center">
                                                                {DateFormaterMonth(
                                                                    row?.project_end_date
                                                                )}
                                                            </td>
                                        
                                                            <td className="text-center">
                                                                <span className={Classes(row.project_status)}>
                                                                    {Labels(row.project_status)}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            ) : (
                                                <tbody>
                                                    <tr>
                                                        <td colSpan="5">
                                                            <NoRecords />
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            )}
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default PendingProjects;
